import type { PublicSizeResponse } from '../lib/api'
import { formatMoney, formatMoneyRange } from '../lib/format'
import { renderSku } from '../lib/sku-display'
import { COMPARISON_LABELS, COMPARISON_LABELS_SHORT } from '../lib/labels'
import { MethodologyPanel } from './MethodologyPanel'

interface Props {
  result: PublicSizeResponse
  termMonths: 12 | 36 | 60
  isFallback: boolean
  onJumpToCompare: () => void
}

function termLabel(t: 12 | 36 | 60): string {
  return t === 12 ? '12-month' : t === 36 ? '36-month' : '60-month'
}

export function SizerResultCard({ result, termMonths, isFallback, onJumpToCompare }: Props) {
  const rendering = renderSku(result.recommendation.sku)
  const monthly = formatMoneyRange(result.pricing.monthlyLow, result.pricing.monthlyHigh)
  const tcv = formatMoneyRange(result.pricing.tcvLow, result.pricing.tcvHigh)

  // Midpoint-to-midpoint comparison. The prior worst-vs-best banded
  // pairing (awsLow vs cyHigh, awsHigh vs cyLow) crossed margin-band
  // uncertainty with comparison uncertainty and produced a midpoint
  // that compressed toward zero — see docs/tco-engine-state-read-2026-05-13.md.
  const cyMid = (result.pricing.tcvLow + result.pricing.tcvHigh) / 2
  const awsMid =
    (result.comparison.awsGovCloud.tcvLow + result.comparison.awsGovCloud.tcvHigh) / 2
  const awsSavingsPct =
    awsMid > 0 ? Math.round(((awsMid - cyMid) / awsMid) * 100) : 0
  const cyHigherThanAws = awsSavingsPct < 0

  return (
    <div className="bg-white border border-mist rounded-card shadow-card p-6 sm:p-8">
      {isFallback && (
        <div className="mb-5 text-xs font-medium text-navy/70 bg-foam border border-mint/30 rounded px-3 py-2">
          Showing reference values — request a procurement-grade quote for live sizing.
        </div>
      )}

      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-2xl sm:text-3xl font-semibold text-navy">
            {rendering.displayName}
          </h3>
          <div className="font-data text-sm text-navy/60 mt-1">
            {rendering.technicalIdentifier} · {result.recommendation.configSummary}
          </div>
          <div className="font-data text-xs text-mint mt-1">
            operated by CyLogic
          </div>
        </div>
        <div className="text-right">
          <div className="font-data text-3xl sm:text-4xl font-semibold text-navy">{monthly}</div>
          <div className="text-xs text-navy/60 mt-1">/mo, dedicated tenancy</div>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-mist grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-data uppercase tracking-wide text-navy/50">
            {termLabel(termMonths)} TCV
          </div>
          <div className="font-data text-xl text-navy mt-1">{tcv}</div>
        </div>
        <div>
          <div className="text-xs font-data uppercase tracking-wide text-navy/50">
            vs {COMPARISON_LABELS.awsGovCloud}
          </div>
          <button
            onClick={onJumpToCompare}
            className="text-mint hover:underline font-medium text-base mt-1 inline-flex items-center gap-1"
          >
            {cyHigherThanAws
              ? `~${Math.abs(awsSavingsPct)}% higher ${termLabel(termMonths)} TCV →`
              : `~${awsSavingsPct}% lower ${termLabel(termMonths)} TCV →`}
          </button>
          <div className="text-xs text-navy/50 font-data mt-0.5">
            midpoint of CyCloud vs {COMPARISON_LABELS_SHORT.awsGovCloud} TCV
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <CompareBar
          label={COMPARISON_LABELS_SHORT.cyCloud}
          range={`${formatMoney(result.comparison.cyCloud.tcvLow)}–${formatMoney(result.comparison.cyCloud.tcvHigh)}`}
          accent
        />
        <CompareBar
          label={COMPARISON_LABELS_SHORT.awsGovCloud}
          range={`${formatMoney(result.comparison.awsGovCloud.tcvLow)}–${formatMoney(result.comparison.awsGovCloud.tcvHigh)}`}
        />
        <CompareBar
          label={COMPARISON_LABELS_SHORT.azureGov}
          range={`${formatMoney(result.comparison.azureGov.tcvLow)}–${formatMoney(result.comparison.azureGov.tcvHigh)}`}
        />
        <CompareBar
          label={COMPARISON_LABELS_SHORT.diyFederal}
          range={`${formatMoney(result.comparison.diyFederal.tcvLow)}–${formatMoney(result.comparison.diyFederal.tcvHigh)}`}
        />
      </div>

      <p className="mt-5 text-xs text-navy/55">
        Sized as Production profile, {termLabel(termMonths)} term, server VM workload. Adjust inputs above.
      </p>

      <MethodologyPanel methodology={result.methodology} isFallback={isFallback} />
    </div>
  )
}

function CompareBar({ label, range, accent = false }: { label: string; range: string; accent?: boolean }) {
  return (
    <div className={`rounded px-3 py-2 ${accent ? 'bg-foam border border-mint/30' : 'bg-chalk border border-mist'}`}>
      <div className="text-[10px] font-data uppercase tracking-wide text-navy/55 leading-tight">{label}</div>
      <div className={`font-data text-sm ${accent ? 'text-navy font-semibold' : 'text-navy/80'}`}>{range}</div>
    </div>
  )
}
